import React from "react";

/**
 * Drawer navigation icons.
 *
 * A single stroked set drawn on a 24px grid, so every row in the sidebar shares
 * one weight and one optical size. Sized by the `size` prop (17px by default,
 * matching the approved design) and coloured with `currentColor`.
 */

type IconProps = React.SVGProps<SVGSVGElement> & {
  size?: number | string;
};

const Icon: React.FC<IconProps & { children: React.ReactNode }> = ({
  size = 17,
  children,
  ...props
}) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.7}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
    style={{ display: "block", flexShrink: 0 }}
    {...props}
  >
    {children}
  </svg>
);

export const NewConversationIcon: React.FC<IconProps> = (props) => (
  <Icon {...props}>
    <path d="M21 12a8 8 0 0 1-11.6 7.1L3 21l1.9-6.4A8 8 0 1 1 21 12Z" />
  </Icon>
);

export const DashboardsIcon: React.FC<IconProps> = (props) => (
  <Icon {...props}>
    <rect x="3" y="3" width="8" height="9" rx="2" />
    <rect x="13" y="3" width="8" height="5" rx="2" />
    <rect x="13" y="10" width="8" height="11" rx="2" />
    <rect x="3" y="14" width="8" height="7" rx="2" />
  </Icon>
);

export const IntegrationsIcon: React.FC<IconProps> = (props) => (
  <Icon {...props}>
    <path d="M10 3v4a2 2 0 0 1-2 2H4M14 21v-4a2 2 0 0 1 2-2h4M4 15h4a2 2 0 0 1 2 2v4M20 9h-4a2 2 0 0 1-2-2V3" />
  </Icon>
);

export const MemoriesIcon: React.FC<IconProps> = (props) => (
  <Icon {...props}>
    <path d="M12 5a3 3 0 0 0-5.9-.7A3 3 0 0 0 4 9.5a3 3 0 0 0 1.2 4.9A3 3 0 0 0 9 19.4 3 3 0 0 0 12 19M12 5a3 3 0 0 1 5.9-.7A3 3 0 0 1 20 9.5a3 3 0 0 1-1.2 4.9A3 3 0 0 1 15 19.4 3 3 0 0 1 12 19M12 5v14" />
  </Icon>
);

export const NotificationsBellIcon: React.FC<IconProps> = (props) => (
  <Icon {...props}>
    <path d="M18 8a6 6 0 1 0-12 0c0 6-2 7-2 7h16s-2-1-2-7M13.7 21a2 2 0 0 1-3.4 0" />
  </Icon>
);

export const SearchIcon: React.FC<IconProps> = (props) => (
  <Icon strokeWidth={2} {...props}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </Icon>
);

export const LogoutIcon: React.FC<IconProps> = (props) => (
  <Icon {...props}>
    <path d="M15 17l5-5-5-5M20 12H9M12 21H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h6" />
  </Icon>
);

export const PinnedMarkIcon: React.FC<IconProps> = (props) => (
  <Icon size={15} {...props}>
    <path d="M15 3 21 9l-4 1-4.5 4.5L13 19l-2 2-3.5-5L3 21l5-4.5L3 13l2-2 4.5.5L14 7l1-4Z" />
  </Icon>
);

/* Row-menu actions. Same grid and weight as the navigation set above. */

export const PinActionIcon: React.FC<IconProps> = (props) => (
  <Icon size={15} {...props}>
    <path d="M9 4h6l-1 6 3 3v2H7v-2l3-3-1-6Z" />
    <path d="M12 15v5" />
  </Icon>
);

export const UnpinActionIcon: React.FC<IconProps> = (props) => (
  <Icon size={15} {...props}>
    <path d="M9 4h6l-1 6 3 3v2H7v-2l3-3-1-6Z" />
    <path d="M12 15v5" />
    <path d="M4 4l16 16" />
  </Icon>
);

export const RenameActionIcon: React.FC<IconProps> = (props) => (
  <Icon size={15} {...props}>
    <path d="M4 20h4L19.5 8.5a2.1 2.1 0 0 0-3-3L5 17v3Z" />
    <path d="M14.5 6.5l3 3" />
  </Icon>
);

export const DeleteActionIcon: React.FC<IconProps> = (props) => (
  <Icon size={15} {...props}>
    <path d="M4 7h16" />
    <path d="M10 11v6M14 11v6" />
    <path d="M6 7l1 13h10l1-13" />
    <path d="M9.5 7V4.5h5V7" />
  </Icon>
);
